import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';

import {
  Analyzer,
  AnalyzerPropertyConstants,
  InsertAnalyzerLicenseDto,
} from 'types';

import { insertLicense } from '../../api/analyzerLicenses';

import { getAnalyzerType } from '../../utils/analyzerTypes/index';
import { RootState } from '../../app/store';
import { queryClient } from 'lib/react-query';

const analyzersAdapter = createEntityAdapter<Analyzer>();

export const createLicense = createAsyncThunk(
  'analyzers/createLicense',
  async (license: InsertAnalyzerLicenseDto[]) => {
    const response = await insertLicense(license);
    return response;
  }
);

interface ExtraSliceState {
  selectedAnalyzerId: number;
  analyzerPropertyConstants: null | AnalyzerPropertyConstants;
  error: null | string;
}

export const analyzerSlice = createSlice({
  name: 'analyzer',
  initialState: analyzersAdapter.getInitialState({
    selectedAnalyzerId: -1,
    analyzerPropertyConstants: null,
    error: null,
  } as ExtraSliceState),
  reducers: {
    analyzerChanged: (state, { payload }) => {
      state.selectedAnalyzerId = payload;
      if (payload !== -1) {
        const analyzer = state.entities[payload];
        if (analyzer) {
          state.analyzerPropertyConstants = getAnalyzerType(analyzer.type);
        }
      } else {
        state.analyzerPropertyConstants = null;
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createLicense.fulfilled, (state, { payload }) => {
      state.error = null;
      queryClient.invalidateQueries(['analyzerModules']);
    });
  },
});

export const { analyzerChanged } = analyzerSlice.actions;

export default analyzerSlice.reducer;

export const {
  selectById: selectAnalyzerById,
  selectIds: selectAnalyzerIds,
  selectEntities: selectAnalyzerEntities,
  selectAll: selectAllAnalyzers,
  selectTotal: selectTotalAnalyzers,
} = analyzersAdapter.getSelectors((state: RootState) => state.analyzers);

export const selectAnalyzersFromIdArray = createSelector(
  [
    selectAllAnalyzers,
    (state: RootState, analyzerIds: number[]) => analyzerIds,
  ],
  (analyzers, analyzerIds) =>
    analyzers.filter((analyzer) => analyzerIds.includes(analyzer.id))
);
